import React, { useEffect, useState } from 'react';
import ConfirmModal from '../../common/ConfirmModal/ConfirmModal';
import EmptyState from '../../common/EmptyState/EmptyState';
import InfoRow from '../../common/InfoRow/InfoRow';
import Loading from '../../common/Loading/Loading';
import TabBar from '../../common/TabBar/TabBar';
import PhotoGallery from '../PhotoGallery/PhotoGallery';
import { AnamnesisService, PhotoService } from '../../../services';
import type { Anamnesis } from '../../../types/anamnesis';
import type { StudentPhoto } from '../../../types';
import {
  buildAnamnesisAnswerRows,
  buildParqAnswerRows,
  hasAnsweredAnamnesis,
  hasAnsweredParq,
} from '../../../utils/newStudentAnswers';
import styles from './NewStudentAnswersModal.module.css';

type AnswersTab = 'anamnesis' | 'parq' | 'photos';

const TABS = [
  { key: 'anamnesis', label: 'Anamnese' },
  { key: 'parq', label: 'PAR-Q' },
  { key: 'photos', label: 'Fotos' },
] as const;

const MODAL_WIDTH_PX = 720;

interface NewStudentAnswersModalProps {
  open: boolean;
  studentId: number | null;
  studentName?: string | null;
  onClose: () => void;
}

const NewStudentAnswersModal: React.FC<NewStudentAnswersModalProps> = ({
  open,
  studentId,
  studentName,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<AnswersTab>('anamnesis');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anamnesis, setAnamnesis] = useState<Anamnesis | null>(null);
  const [photos, setPhotos] = useState<StudentPhoto[]>([]);

  useEffect(() => {
    if (!open || !studentId) {
      return;
    }

    let cancelled = false;
    setActiveTab('anamnesis');
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const [anamnesisResult, photosResult] = await Promise.all([
          AnamnesisService.findByStudentId(studentId),
          PhotoService.getBodyPhotos(studentId),
        ]);

        if (cancelled) {
          return;
        }

        setAnamnesis(anamnesisResult);
        setPhotos(photosResult);
      } catch (loadError) {
        if (!cancelled) {
          setAnamnesis(null);
          setPhotos([]);
          setError(loadError instanceof Error
            ? loadError.message
            : 'Não foi possível carregar as respostas da aluna.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, studentId]);

  const anamnesisRows = buildAnamnesisAnswerRows(anamnesis);
  const parqRows = buildParqAnswerRows(anamnesis);

  return (
    <ConfirmModal
      open={open}
      title={studentName ? `Respostas — ${studentName}` : 'Respostas da aluna'}
      variant="info"
      icon="fact_check"
      confirmLabel="Fechar"
      hideCancel
      width={MODAL_WIDTH_PX}
      onCancel={onClose}
      onConfirm={onClose}
    >
      <div className={styles.content}>
        <TabBar
          tabs={[...TABS]}
          activeKey={activeTab}
          onTabChange={(key) => setActiveTab(key as AnswersTab)}
          className={styles.tabs}
        />

        <div className={styles.body}>
          {loading && <Loading />}

          {!loading && error && (
            <div className={styles.empty}>
              <EmptyState message={error} showImage={false} />
            </div>
          )}

          {!loading && !error && activeTab === 'anamnesis' && (
            hasAnsweredAnamnesis(anamnesisRows) ? (
              <div className={styles.rows}>
                {anamnesisRows.map((row) => (
                  <InfoRow key={row.key} label={row.label} value={row.value} />
                ))}
              </div>
            ) : (
              <div className={styles.empty}>
                <EmptyState message="Anamnese ainda não foi respondida." showImage={false} />
              </div>
            )
          )}

          {!loading && !error && activeTab === 'parq' && (
            hasAnsweredParq(parqRows) ? (
              <div className={styles.rows}>
                {parqRows.map((row) => (
                  <InfoRow key={row.key} label={row.label} value={row.value} />
                ))}
              </div>
            ) : (
              <div className={styles.empty}>
                <EmptyState message="PAR-Q ainda não foi respondido." showImage={false} />
              </div>
            )
          )}

          {!loading && !error && activeTab === 'photos' && (
            photos.length > 0 ? (
              <PhotoGallery photos={photos} />
            ) : (
              <div className={styles.empty}>
                <EmptyState message="Nenhuma foto enviada." showImage={false} />
              </div>
            )
          )}
        </div>
      </div>
    </ConfirmModal>
  );
};

export default NewStudentAnswersModal;
